import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, Pencil, Trash2, Tag, Euro, Percent, Eye, Calendar, Users
} from "lucide-react";

const emptyForm = {
  code: "", description: "", discount_type: "percentage", discount_value: 0,
  valid_from: "", valid_to: "", max_uses: 0, single_use_per_customer: false,
  min_booking_amount: 0, applicable_service_ids: [], applicable_category_ids: [], is_active: true
};

export default function DiscountCodesManager({ apiBasePath, services = [], categories = [] }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [usageData, setUsageData] = useState({ code: null, usages: [] });

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}${apiBasePath}/discount-codes`, { headers });
      setCodes(res.data);
    } catch { /* empty */ }
    setLoading(false);
  };

  useEffect(() => { fetchCodes(); }, [apiBasePath]);

  const handleSave = async () => {
    if (!form.code.trim()) { toast.error("Il codice è obbligatorio"); return; }
    if (form.discount_value <= 0) { toast.error("Il valore sconto deve essere maggiore di 0"); return; }
    try {
      if (editing) {
        await axios.put(`${API}${apiBasePath}/discount-codes/${editing.discount_id}`, form, { headers });
        toast.success("Codice aggiornato!");
      } else {
        await axios.post(`${API}${apiBasePath}/discount-codes`, form, { headers });
        toast.success("Codice creato!");
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...emptyForm });
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Eliminare questo codice sconto?")) return;
    try {
      await axios.delete(`${API}${apiBasePath}/discount-codes/${id}`, { headers });
      toast.success("Codice eliminato!");
      fetchCodes();
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  const handleToggle = async (code) => {
    try {
      await axios.put(`${API}${apiBasePath}/discount-codes/${code.discount_id}`, { is_active: !code.is_active }, { headers });
      fetchCodes();
    } catch { toast.error("Errore"); }
  };

  const openUsage = async (code) => {
    try {
      const res = await axios.get(`${API}${apiBasePath}/discount-codes/${code.discount_id}/usage`, { headers });
      setUsageData(res.data);
      setUsageDialogOpen(true);
    } catch { toast.error("Errore nel caricamento utilizzi"); }
  };

  const openEdit = (code) => {
    setEditing(code);
    setForm({
      code: code.code, description: code.description || "", discount_type: code.discount_type,
      discount_value: code.discount_value, valid_from: code.valid_from || "", valid_to: code.valid_to || "",
      max_uses: code.max_uses, single_use_per_customer: code.single_use_per_customer || false,
      min_booking_amount: code.min_booking_amount || 0,
      applicable_service_ids: code.applicable_service_ids || [],
      applicable_category_ids: code.applicable_category_ids || [],
      is_active: code.is_active
    });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Tag className="h-5 w-5" /> Codici Sconto
          </h3>
          <p className="text-sm text-slate-500">{codes.length} codici sconto configurati</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ ...emptyForm }); setDialogOpen(true); }} data-testid="create-discount-btn">
          <Plus className="h-4 w-4 mr-2" /> Nuovo Codice
        </Button>
      </div>

      {/* Codes Table */}
      {codes.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400">
          <Tag className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>Nessun codice sconto. Crea il primo!</p>
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Codice</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Valore</TableHead>
                  <TableHead>Validità</TableHead>
                  <TableHead>Utilizzi</TableHead>
                  <TableHead>Min. Ordine</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.map(code => {
                  const isExpired = code.valid_to && new Date(code.valid_to) < new Date();
                  const isExhausted = code.max_uses > 0 && code.current_uses >= code.max_uses;
                  return (
                    <TableRow key={code.discount_id} className={`${!code.is_active || isExpired || isExhausted ? 'opacity-60' : ''}`} data-testid={`discount-row-${code.discount_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono text-sm">{code.code}</Badge>
                          {code.description && <span className="text-xs text-slate-400 truncate max-w-[150px]">{code.description}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {code.discount_type === 'percentage' ? <Percent className="h-4 w-4 text-blue-500" /> : <Euro className="h-4 w-4 text-green-500" />}
                      </TableCell>
                      <TableCell className="font-semibold">
                        {code.discount_type === 'percentage' ? `${code.discount_value}%` : `€${code.discount_value.toFixed(2)}`}
                      </TableCell>
                      <TableCell className="text-xs">
                        {code.valid_from || code.valid_to ? (
                          <div className="flex flex-col">
                            {code.valid_from && <span>Da: {code.valid_from}</span>}
                            {code.valid_to && <span className={isExpired ? 'text-red-500 font-medium' : ''}>A: {code.valid_to}</span>}
                          </div>
                        ) : <span className="text-slate-400">Sempre</span>}
                      </TableCell>
                      <TableCell>
                        <span className={`text-sm font-medium ${isExhausted ? 'text-red-500' : ''}`}>
                          {code.current_uses}/{code.max_uses || '∞'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {code.min_booking_amount > 0 ? `€${code.min_booking_amount.toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell>
                        <Switch checked={code.is_active} onCheckedChange={() => handleToggle(code)} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openUsage(code)} title="Vedi utilizzi" data-testid={`usage-btn-${code.discount_id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(code)} data-testid={`edit-discount-${code.discount_id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDelete(code.discount_id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifica Codice Sconto' : 'Nuovo Codice Sconto'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Codice *</Label>
                <Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="ES: ESTATE25" className="font-mono" data-testid="discount-code-input" />
              </div>
              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Sconto estate..." />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo sconto</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({...form, discount_type: v})}>
                  <SelectTrigger data-testid="discount-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentuale (%)</SelectItem>
                    <SelectItem value="fixed">Fisso (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Valore *</Label>
                <Input type="number" min="0" step="0.01" value={form.discount_value} onChange={(e) => setForm({...form, discount_value: parseFloat(e.target.value) || 0})} data-testid="discount-value-input" />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Valido dal</Label>
                <Input type="date" value={form.valid_from} onChange={(e) => setForm({...form, valid_from: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Valido fino al</Label>
                <Input type="date" value={form.valid_to} onChange={(e) => setForm({...form, valid_to: e.target.value})} />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max utilizzi (0 = illimitato)</Label>
                <Input type="number" min="0" value={form.max_uses} onChange={(e) => setForm({...form, max_uses: parseInt(e.target.value) || 0})} data-testid="max-uses-input" />
              </div>
              <div className="space-y-2">
                <Label>Importo minimo ordine (€)</Label>
                <Input type="number" min="0" step="0.01" value={form.min_booking_amount} onChange={(e) => setForm({...form, min_booking_amount: parseFloat(e.target.value) || 0})} data-testid="min-amount-input" />
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Checkbox checked={form.single_use_per_customer} onCheckedChange={(v) => setForm({...form, single_use_per_customer: v})} id="single_use" data-testid="single-use-checkbox" />
              <label htmlFor="single_use" className="text-sm cursor-pointer">Uso singolo per cliente (basato su email)</label>
            </div>
            
            {/* Applicable Services */}
            {services.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">Servizi applicabili (vuoto = tutti)</Label>
                <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {services.filter(s => s.is_active !== false).map(s => (
                    <label key={s.service_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-2 py-1 rounded">
                      <input type="checkbox" className="h-3.5 w-3.5 rounded"
                        checked={form.applicable_service_ids.includes(s.service_id)}
                        onChange={(e) => {
                          const ids = e.target.checked
                            ? [...form.applicable_service_ids, s.service_id]
                            : form.applicable_service_ids.filter(id => id !== s.service_id);
                          setForm({...form, applicable_service_ids: ids});
                        }}
                      />
                      {s.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            {/* Applicable Categories */}
            {categories.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">Categorie applicabili (vuoto = tutte)</Label>
                <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {categories.map(c => (
                    <label key={c.category_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-2 py-1 rounded">
                      <input type="checkbox" className="h-3.5 w-3.5 rounded"
                        checked={form.applicable_category_ids.includes(c.category_id)}
                        onChange={(e) => {
                          const ids = e.target.checked
                            ? [...form.applicable_category_ids, c.category_id]
                            : form.applicable_category_ids.filter(id => id !== c.category_id);
                          setForm({...form, applicable_category_ids: ids});
                        }}
                      />
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color || '#6B7280' }} />
                      {c.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSave} data-testid="save-discount-btn">{editing ? 'Aggiorna' : 'Crea Codice'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usage Dialog */}
      <Dialog open={usageDialogOpen} onOpenChange={setUsageDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Utilizzi: <Badge variant="outline" className="font-mono text-base">{usageData.code?.code}</Badge>
            </DialogTitle>
          </DialogHeader>
          {usageData.code && (
            <div className="flex gap-4 text-sm mb-4">
              <span>Tipo: <strong>{usageData.code.discount_type === 'percentage' ? `${usageData.code.discount_value}%` : `€${usageData.code.discount_value}`}</strong></span>
              <span>Utilizzi: <strong>{usageData.code.current_uses}/{usageData.code.max_uses || '∞'}</strong></span>
              <span>Totale risparmiato: <strong>€{usageData.usages.reduce((s, u) => s + (u.discount_amount || 0), 0).toFixed(2)}</strong></span>
            </div>
          )}
          {usageData.usages.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Users className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>Nessun utilizzo registrato</p>
            </div>
          ) : (
            <Table data-testid="usage-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Prezzo orig.</TableHead>
                  <TableHead>Sconto</TableHead>
                  <TableHead>Prezzo finale</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usageData.usages.map(u => (
                  <TableRow key={u.usage_id}>
                    <TableCell className="text-xs">{new Date(u.used_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</TableCell>
                    <TableCell className="font-medium">{u.customer_name}</TableCell>
                    <TableCell className="text-xs text-slate-500">{u.customer_email}</TableCell>
                    <TableCell>€{(u.original_price || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-green-600 font-medium">-€{(u.discount_amount || 0).toFixed(2)}</TableCell>
                    <TableCell className="font-bold">€{(u.final_price || 0).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
